/**
 * Chilean RUT (Rol Único Tributario) Validation Utilities
 * 
 * Implements the Chilean modulo 11 algorithm for RUT validation.
 * RUT format: XX.XXX.XXX-X where the last X is the check digit (0-9 or K)
 */

/**
 * Calculates the check digit (dígito verificador) for a RUT body
 * using the Chilean modulo 11 algorithm.
 * 
 * Algorithm:
 * 1. Multiply each digit by weights [2,3,4,5,6,7] cycling from right to left
 * 2. Sum all products
 * 3. Calculate 11 - (sum mod 11)
 * 4. Return '0' if result is 11, 'K' if result is 10, otherwise the digit
 * 
 * @param {string} rutBody - The RUT body (7-8 digits, no check digit)
 * @returns {string} The calculated check digit ('0'-'9' or 'K')
 */
export function calculateCheckDigit(rutBody) {
  // Remove any non-digit characters
  const cleanBody = rutBody.replace(/\D/g, '');
  
  if (!cleanBody || cleanBody.length < 7 || cleanBody.length > 8) {
    return '';
  }
  
  const weights = [2, 3, 4, 5, 6, 7];
  let sum = 0;
  let weightIndex = 0;
  
  // Process digits from right to left
  for (let i = cleanBody.length - 1; i >= 0; i--) {
    const digit = parseInt(cleanBody[i], 10);
    sum += digit * weights[weightIndex];
    weightIndex = (weightIndex + 1) % weights.length;
  }
  
  const remainder = sum % 11;
  const result = 11 - remainder;
  
  if (result === 11) return '0';
  if (result === 10) return 'K';
  return result.toString();
}

/**
 * Formats a RUT string to the standard Chilean format: XX.XXX.XXX-X
 * 
 * @param {string} rut - The RUT to format (with or without dots/dash)
 * @returns {string} The formatted RUT or empty string if invalid format
 */
export function formatRut(rut) {
  if (!rut || typeof rut !== 'string') {
    return '';
  }
  
  // Remove all non-alphanumeric characters except K/k
  let clean = rut.replace(/[^0-9kK]/g, '').toUpperCase();
  
  if (clean.length < 2) {
    return '';
  }
  
  // Extract check digit (last character)
  const checkDigit = clean.slice(-1);
  const body = clean.slice(0, -1);
  
  // Validate body is all digits
  if (!/^\d+$/.test(body)) {
    return '';
  }
  
  // Validate check digit is digit or K
  if (!/^[0-9K]$/.test(checkDigit)) {
    return '';
  }
  
  // Format body with dots (from right to left, groups of 3)
  let formattedBody = '';
  for (let i = body.length - 1, count = 0; i >= 0; i--, count++) {
    if (count > 0 && count % 3 === 0) {
      formattedBody = '.' + formattedBody;
    }
    formattedBody = body[i] + formattedBody;
  }
  
  return `${formattedBody}-${checkDigit}`;
}

/**
 * Validates a Chilean RUT
 * 
 * @param {string} rut - The RUT to validate (with or without formatting)
 * @returns {Object} Validation result with isValid, formatted, and error fields
 * @returns {boolean} result.isValid - Whether the RUT is valid
 * @returns {string} result.formatted - The formatted RUT (XX.XXX.XXX-X) or empty string
 * @returns {string} [result.error] - Error message in Spanish if invalid
 */
export function validateRut(rut) {
  // Handle empty or invalid input
  if (!rut || typeof rut !== 'string') {
    return {
      isValid: false,
      formatted: '',
      error: 'El RUT es requerido'
    };
  }
  
  // Remove all non-alphanumeric characters except K/k
  const clean = rut.replace(/[^0-9kK]/g, '').toUpperCase();
  
  // Check minimum length (at least 8 characters: 7 digits + 1 check digit)
  if (clean.length < 8 || clean.length > 9) {
    return {
      isValid: false,
      formatted: '',
      error: 'Formato de RUT inválido'
    };
  }
  
  // Extract body and check digit
  const checkDigit = clean.slice(-1);
  const body = clean.slice(0, -1);
  
  // Validate body contains only digits
  if (!/^\d+$/.test(body)) {
    return {
      isValid: false,
      formatted: '',
      error: 'Formato de RUT inválido'
    };
  }
  
  // Validate check digit is digit or K
  if (!/^[0-9K]$/.test(checkDigit)) {
    return {
      isValid: false,
      formatted: '',
      error: 'Formato de RUT inválido'
    };
  }
  
  // Calculate expected check digit
  const expectedCheckDigit = calculateCheckDigit(body);
  
  // Validate check digit
  if (checkDigit !== expectedCheckDigit) {
    return {
      isValid: false,
      formatted: formatRut(rut),
      error: 'RUT inválido - dígito verificador incorrecto'
    };
  }
  
  // Valid RUT
  return {
    isValid: true,
    formatted: formatRut(rut),
    error: undefined
  };
}

export default {
  validateRut,
  formatRut,
  calculateCheckDigit
};
